/*
 * UtilisationOverview.tsx
 * Author: fwunderlich
 * Date: 01.12.2023
 *
 * Copyright: DMT GmbH & Co. KG
 */

import React, {useEffect, useRef, useState} from 'react';
import CustomDataTable, {ColumnObject} from '../../global/CustomDataTable';
import {UtilisationDialogEditForm} from './UtilisationDialogEditForm';
import {checkPermission} from '../../../functions/functionLibrary';
import {permissions} from '../../../config/permissions';
import {useGetUtilisationOverviewQuery} from '../../../redux/rtk/injectedUtilisationApi';
import {Utilisation, getColumnsUtilisation, getFiltersUtilisation, Measure} from '../../functions/ProjectMasterData';
import {useMapper} from '../../hooks/useMapper';
import {useTranslation} from 'react-i18next';
import {useGetPersonenTypesQuery, useSetStatusMutation} from '../../../redux/rtk/injectedMappingApi';
import {classNames} from 'primereact/utils';
import {useAppSelector} from '../../../redux/hooks';
import {Button} from 'primereact/button';
import {Menu} from 'primereact/menu';
import {CustomConfirmDialog} from '../../global/CustomConfirmDialog';
import {ProjectList} from '../../globalData/ProjectList';
import {MeasureOverview} from '../measure/MeasureOverview';
import {exportCSV} from '../../functions/Global';


type Props = {
    projectId: string,
    editable: boolean,
    expandable: boolean,
    hideGlobalFilter: boolean,
    onFinished?: (data: Utilisation) => void,
    showColumnFilters: boolean,
    hideButtons?: boolean,
    mapping?: any,
    selectable?: boolean,
    multiSelection?: boolean,
    onSelection?: (e: Utilisation[]) => void,
    className?: string,
    runRefetch?: boolean,
    scrollHeight?: string,
    skeletonCount?: number,
    showHeader?: boolean,
    skip?: boolean
}

const DATATABLE_ID = 'utilisationDataTable';

export const UtilisationOverview = (props: Props) => {
    const {t} = useTranslation(['common', 'projectMasterData']);
    const status = useAppSelector((state: any) => state.importData);

    const menu = useRef<any>(null);

    const [values, setValues] = useState<any>([]);
    const [selectedValue, setSelectedValue] = useState<Utilisation | undefined>(undefined);
    const [showCreateUtilisation, setShowCreateUtilisation] = useState<boolean>(false);
    const [showEditUtilisationSelection, setShowEditUtilisationSelection] = useState<boolean>(false);
    const [editValue, setEditValue] = useState<boolean>(false);
    const [showAddUtilisationToProject, setShowAddUtilisationToProject] = useState<boolean>(false);

    const [dataLoaded, setDataLoaded] = useState(props.skip || false);
    const [selection, setSelection] = useState<Utilisation[]>([]);
    const [expandedRows, setExpandedRows] = useState<any>(null);
    const [columns, setColumns] = useState<ColumnObject[]>([]);
    const [returnedData, setReturnedData] = useState<Utilisation>();

    const mappedArray = useMapper(returnedData, values, 'massnahme_id');

    const {
        data: utilisation = undefined, isLoading,
        isSuccess,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetUtilisationOverviewQuery({
        projectId: props.projectId,
        mapping: props.mapping
    }, {skip: props.skip != null ? props.skip : false});

    if (isError) {
        console.log({error});
    }

    useEffect(() => {
        if (window.location.hash.indexOf('_') > -1 && utilisation) {
            const hashElements = window.location.hash.split('_');
            if (hashElements.length > 2) {
                const id = hashElements[2];
                const sUtil = utilisation.find((item: Utilisation) => {
                    return item.inanspruchnahme_id === id;
                });
                if (sUtil) {
                    setSelectedValue(sUtil);
                    setEditValue(true);
                }
            }
        }
    }, [utilisation]);

    useEffect(() => {
        setColumns(getColumnsUtilisation(t, true));
        if (utilisation) {
            const expandedRowsTemp: any[] = [];
            const newArray = JSON.parse(JSON.stringify(utilisation));
            newArray.map((r: Utilisation) => {
                    if (r.massnahme && r.massnahme[0] !== null)
                        r.expandableElement = (
                            <><MeasureOverview
                                hideButtons={true}
                                projectId={props.projectId}
                                mapping={{inanspruchnahme_id: r.inanspruchnahme_id}}
                                editable={false}
                                expandable={false}
                                hideGlobalFilter={false}
                                showColumnFilters={false}
                                skeletonCount={1}
                                selectable={true}
                            />
                                {props.projectId === '' &&
                                <ProjectList projectId={props.projectId} mapping={{flurstueck_id: r.flurstueck_id}}
                                             hideGlobalFilter={false} showColumnFilters={true}/>
                                }
                            </>);

                    setExpandedRows(expandedRowsTemp);
                }
            );

            setValues(newArray);
            setDataLoaded(true);
        }
    }, [utilisation]);

    useEffect(() => {
            if (typeof returnedData !== 'undefined') {
                setValues(mappedArray);
            }
        }
        , [returnedData]);

    useEffect(() => {
            if (props.onSelection) {
                props.onSelection(selection);
            }
        }
        , [selection]);

    const hideDialog = () => {
        setShowEditUtilisationSelection(false);
        setShowAddUtilisationToProject(false);
    };


    const getButtons = () => {
        const result: any[] = [];

        result.push(
            {
                label: t('customDataTable:tooltips.exportAsCSV'),
                icon: 'pi pi-file-export',
                disabled: false,
                command: () => {
                    exportCSV(t, values, DATATABLE_ID, columns);
                }
            },);

        return result;
    };

    const onClickCreateUtilisation = () => {
        setShowCreateUtilisation(true);
    };

    const onClickEditSelection = () => {
        setShowEditUtilisationSelection(true);
    };

    return (<div className={classNames('h-full', props.className)}><CustomDataTable
            id={DATATABLE_ID}
            header={
                <div className={'grid'}>
                    {props.showHeader !== false ? <div className={'col'}>
                        <h4 className={'m-0'}>{t('projectMasterData:utilisation.plural')}</h4>
                    </div> : ''}
                    {props.hideButtons !== true && <div className={'col flex justify-content-end flex-wrap mb-auto'}>
                        <Button type={'button'}
                                className={'p-button-success p-button-icon-only p-button-outlined mx-2'}
                                icon={'pi pi-plus'} onClick={onClickCreateUtilisation}/>
                        <div className={'inline mx-2'}>
                            <Menu model={getButtons()} popup ref={menu} id="popup_menu"/>
                            <Button className={'p-button-icon-only p-button-outlined'} icon="pi pi-angle-down"
                                    onClick={(event) => {
                                        if (menu.current) {
                                            menu.current.toggle(event);
                                        }
                                    }} aria-controls="popup_menu" aria-haspopup/>
                        </div>
                    </div>}
                </div>}
            className={props.className}
            customButtons={[{
                className: 'p-button-danger',
                icons: [{key: null, value: null, icon: <i className={'pi pi-trash'}/>}],
                tooltip: t('delete'),
                tooltipOptions: {position: 'top'},
                onClick: (data: any) => {
                    CustomConfirmDialog({
                        message: t('globalData:dialogs.deleteUtilisationDialog.singular'),
                        header: t('confirmDialog:deleteDialogHeader'),
                        translation: t,
                        onConfirm: () => {
                            // deleteParcel({
                            //     project_id: props.projectId ? props.projectId : '',
                            //     flurstueck_id: data.flurstueck_id,
                            //     common_data: true
                            // });
                        }
                    });
                },
                permitted: checkPermission(permissions.editUtilisation) && props.editable,
            }]}
            columns={columns}
            scrollHeight={props.scrollHeight}
            sortable={true}
            value={values}
            filters={getFiltersUtilisation()}
            expandable={props.expandable}
            expandedRows={expandedRows}
            paginator={true}
            paginatorHideAll={true}
            displayColumnFilter={props.showColumnFilters}
            selectionMode={props.selectable ? 'checkbox' : undefined}
            selection={props.selectable ? selection : undefined}
            onSelectionChange={props.selectable ? (e) => {
                if (props.multiSelection) {
                    setSelection(e.value);
                } else {
                    setSelection([e.value[e.value.length - 1]]);
                }
            } : undefined}
            editable={checkPermission(permissions.editUtilisation) && props.editable}
            onEditClick={(data: any) => {
                setSelectedValue(data);
                setEditValue(true);
            }}
            ableToUpdate={checkPermission(permissions.editUtilisation) && props.editable}
            dataloaded={dataLoaded}
            hideGlobalFilter={props.hideGlobalFilter}
            skeletonCount={props.skeletonCount}
        />
            <UtilisationDialogEditForm
                selectedUtil={selectedValue}
                setSelectedUtil={setSelectedValue}
                show={editValue}
                setShow={setEditValue}
                projectId={props.projectId}
                onFinished={(data: Utilisation) => {
                    setReturnedData(data);
                }}
            />
            {showCreateUtilisation && <UtilisationDialogEditForm
                projectId={props.projectId}
                show={showCreateUtilisation}
                setShow={setShowCreateUtilisation}
            />}
        </div>
    );
};
